import { Flex, Grid, Table, Typography, theme } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import type { HistoryRow } from './historyApi'
import { kindLabel, packageLine, signedMoney } from './historyLabels'

function Amount({ value }: { value: Money }) {
  const { token } = theme.useToken()
  const color = value.amount > 0 ? token.colorSuccess : value.amount < 0 ? token.colorError : undefined
  return (
    <Typography.Text strong style={{ color, whiteSpace: 'nowrap' }}>
      {signedMoney(value)}
    </Typography.Text>
  )
}

function Operation({ row }: { row: HistoryRow }) {
  const { t } = useTranslation()
  const pkg = packageLine(row)
  return (
    <Flex vertical style={{ minWidth: 0 }}>
      <Typography.Text>{kindLabel(row.kind, t)}</Typography.Text>
      {pkg ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {pkg}
        </Typography.Text>
      ) : null}
    </Flex>
  )
}

function RowCard({ row }: { row: HistoryRow }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <div style={{ borderBottom: `1px solid ${token.colorSplit}`, padding: '12px 16px' }}>
      <Flex justify="space-between" align="start" gap={12}>
        <Operation row={row} />
        <Flex vertical align="end" style={{ flexShrink: 0 }}>
          <Amount value={row.amount} />
          <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {t('history.balanceAfterShort', { amount: formatMoneyValue(row.balanceAfter) })}
          </Typography.Text>
        </Flex>
      </Flex>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {formatDateTime(row.at)}
        {row.reference ? ` · ${t('history.documentShort', { no: row.reference })}` : null}
      </Typography.Text>
    </div>
  )
}

export function HistoryRows({ rows }: { rows: readonly HistoryRow[] }) {
  const { t } = useTranslation()
  const isMobile = Grid.useBreakpoint().md === false

  if (isMobile) {
    return (
      <div>
        {rows.map((row) => (
          <RowCard key={row.txnId} row={row} />
        ))}
      </div>
    )
  }

  const columns: ColumnsType<HistoryRow> = [
    {
      key: 'at',
      title: t('history.col.at'),
      width: 180,
      render: (_v, row) => formatDateTime(row.at),
    },
    {
      key: 'operation',
      title: t('history.col.operation'),
      render: (_v, row) => <Operation row={row} />,
    },
    {
      key: 'amount',
      title: t('history.col.amount'),
      align: 'right',
      render: (_v, row) => <Amount value={row.amount} />,
    },
    {
      key: 'balanceAfter',
      title: t('history.col.balanceAfter'),
      align: 'right',
      render: (_v, row) => formatMoneyValue(row.balanceAfter),
    },
    {
      key: 'reference',
      title: t('history.col.document'),
      ellipsis: true,
      render: (_v, row) => row.reference ?? t('common.dash'),
    },
  ]

  return (
    <Table<HistoryRow>
      rowKey="txnId"
      columns={columns}
      dataSource={[...rows]}
      pagination={false}
      scroll={{ x: true }}
    />
  )
}
